import * as Sentry from '@sentry/react-native';
import Cookies from 'js-cookie';
import Constants from 'src/core/utils/Constants';
import type { AuthTokenRead, SecureStorageServiceContract } from 'src/data/services/types/SecureStorageServiceTypes';

/**
 * Web sibling of SecureStorageService.ts. expo-secure-store talks to the Keychain and the
 * Android Keystore, neither of which a browser has, so the token lives in a cookie here.
 * The bundler resolves this file for platform === 'web' only.
 *
 * The cookie is host-only: no Domain attribute, so it stays on the exact host that set it
 * instead of being handed to every subdomain of the parent domain.
 */

// HttpOnly is impossible here: ApiService reads the token from JavaScript to put it in the
// request header, so the document itself has to be able to see the cookie.
//
// No Expires either, which makes this a session cookie: closing the browser signs you out,
// where the keychain on native keeps you signed in until you log out. That difference is
// deliberate -- a shared or public computer is a browser problem and not a phone problem, and
// the token's own lifetime is not something this side knows. Measured in Chrome: the cookie does
// survive inside the drawer's cross-origin iframe, so the session is shared between a direct
// visit and the panel (the embedded app's own `GET /api/user` comes back 200).
const cookieAttributes = { secure: true, sameSite: 'strict' } as const;

function report(error: unknown, step: string): void {
	Sentry.captureException(error, { tags: { context: 'secure-storage', step } });
}

/**
 * Cookies answer synchronously where the keychain does not, so the actual read lives here
 * and the promise-returning methods below only wrap it.
 */
function readToken(): AuthTokenRead {
	try {
		return { ok: true, token: Cookies.get(Constants.secureStorageAuthToken) ?? null };
	} catch (error) {
		// A sandboxed embed throws a SecurityError on document.cookie. That is a read that
		// failed, which is not the same as being signed out.
		report(error, 'get');

		return { ok: false };
	}
}

const SecureStorageService: SecureStorageServiceContract = {
	readAuthToken(): Promise<AuthTokenRead> {
		return Promise.resolve(readToken());
	},

	getAuthToken(): Promise<string | null> {
		const result = readToken();

		return Promise.resolve(result.ok ? result.token : null);
	},

	setAuthToken(token: string): Promise<boolean> {
		try {
			Cookies.set(Constants.secureStorageAuthToken, token, cookieAttributes);

			// A browser can drop the cookie without raising anything: `secure` is refused
			// over plain http, and an embedded document may have storage access denied. So
			// read it back instead of reporting a write that never landed.
			return Promise.resolve(Cookies.get(Constants.secureStorageAuthToken) === token);
		} catch (error) {
			report(error, 'set');

			return Promise.resolve(false);
		}
	},

	clearAuthToken(): Promise<void> {
		try {
			Cookies.remove(Constants.secureStorageAuthToken, cookieAttributes);
		} catch (error) {
			report(error, 'clear');
		}

		return Promise.resolve();
	},
};

export type { AuthTokenRead };
export default SecureStorageService;
